import * as React from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import TagIcon        from "@mui/icons-material/Tag";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import LockIcon        from "@mui/icons-material/Lock";

export default function ConsumerNumberField({
  cardData,
  consumerNumber,
  consumerNumberError,
  autoFillLoading,
  onChange,
  onAutoFill,
  onReset,          // called when user wants to change consumer number after fetch
  consumerNumberLength = 13,
  // Fetch state passed from parent
  billFetched,
  fetchedAmount,
  fetchedDueDate,
}) {
  const color     = cardData?.color || "#6366F1";
  const isEnabled = consumerNumber.length === consumerNumberLength && !autoFillLoading && !billFetched;

  // Strip commas/formatting — show plain number only
  const displayAmount = fetchedAmount != null
    ? String(fetchedAmount).replace(/,/g, "")
    : null;

  return (
    <Box sx={{ px: 4, pt: 3, pb: 0 }}>

      {/* ── Consumer number input ─────────────────────────────────────────── */}
      <Box
        sx={{
          p: 2.5, borderRadius: 3, mb: 2,
          backgroundColor: billFetched ? `${color}06` : "#f8fafc",
          border: `2px solid ${
            consumerNumberError ? "#ef4444"
            : billFetched       ? color
            : consumerNumber.length === consumerNumberLength ? color
            : "#e2e8f0"
          }`,
          transition: "all 0.25s",
          ...(!billFetched && {
            "&:focus-within": {
              borderColor: consumerNumberError ? "#ef4444" : color,
              backgroundColor: "#fff",
              boxShadow: `0 0 0 3px ${consumerNumberError ? "#ef444420" : color + "20"}`,
            },
          }),
        }}
      >
        {/* Label row */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TagIcon sx={{ fontSize: 20, color }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "0.875rem" }}>
              Consumer Number
            </Typography>
            <Chip
              label="Optional"
              size="small"
              sx={{ height: 20, fontSize: "0.65rem", fontWeight: 700, backgroundColor: "#f1f5f9", color: "#64748b", "& .MuiChip-label": { px: 1 } }}
            />
          </Box>
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, fontSize: "0.72rem", color: consumerNumber.length === consumerNumberLength ? color : "#cbd5e1" }}
          >
            {consumerNumber.length}/{consumerNumberLength}
          </Typography>
        </Box>

        {/* Input + button row */}
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={`Enter ${consumerNumberLength}-digit number (optional)`}
            value={consumerNumber}
            onChange={onChange}
            inputProps={{ maxLength: consumerNumberLength, inputMode: "numeric" }}
            error={!!consumerNumberError}
            helperText={consumerNumberError || " "}
            FormHelperTextProps={{ sx: { mt: 0.5, fontSize: "0.72rem" } }}
            InputProps={{
              // NOT readOnly when fetched — user can still type a new number
              // Parent's onChange handler will reset fetch state automatically
              startAdornment: (
                <InputAdornment position="start">
                  <TagIcon sx={{ fontSize: 18, color: consumerNumber.length === consumerNumberLength ? color : "#94a3b8" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#fff", borderRadius: 2, fontWeight: 600,
                letterSpacing: consumerNumber.length > 0 ? "0.08em" : "normal",
                "& fieldset":             { borderColor: "transparent" },
                "&:hover fieldset":       { borderColor: "transparent" },
                "&.Mui-focused fieldset": { borderColor: "transparent" },
                "&.Mui-error fieldset":   { borderColor: "transparent" },
              },
            }}
          />
          {/* Show Auto-Fill button when not fetched yet; show "Change" when fetched */}
          {!billFetched ? (
            <Button
              variant="contained"
              startIcon={autoFillLoading ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <AutoFixHighIcon />}
              onClick={onAutoFill}
              disabled={!isEnabled}
              sx={{
                whiteSpace: "nowrap", flexShrink: 0, borderRadius: 2.5,
                px: 2.5, py: 1, mb: "22px", fontWeight: 700, textTransform: "none", fontSize: "0.85rem",
                backgroundColor: isEnabled ? color : "#e2e8f0",
                color: isEnabled ? "#fff" : "#94a3b8",
                boxShadow: isEnabled ? `0 4px 14px ${color}50` : "none",
                "&:hover": { opacity: 0.88 },
                "&:disabled": { backgroundColor: "#e2e8f0", color: "#94a3b8", boxShadow: "none" },
              }}
            >
              {autoFillLoading ? "Fetching…" : "Auto-Fill"}
            </Button>
          ) : (
            // "Change" button — clears fetch state so user can enter a new number
            <Button
              variant="outlined"
              onClick={onReset}
              sx={{
                whiteSpace: "nowrap", flexShrink: 0, borderRadius: 2.5,
                px: 2, py: 1, mb: "22px", fontWeight: 700, textTransform: "none", fontSize: "0.82rem",
                borderColor: "#ef4444", color: "#ef4444", borderWidth: "2px",
                "&:hover": { backgroundColor: "#fff5f5", borderColor: "#ef4444", borderWidth: "2px" },
              }}
            >
              Change No.
            </Button>
          )}
        </Box>

        {/* Bottom hint */}
        {!billFetched ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <InfoOutlinedIcon sx={{ fontSize: 13, color: "#94a3b8" }} />
            <Typography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.72rem" }}>
              Enter consumer number to auto-fill bill details, or fill the form manually below
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, p: 1, borderRadius: 1.5, bgcolor: `${color}10` }}>
            <CheckCircleIcon sx={{ fontSize: 13, color }} />
            <Typography variant="caption" sx={{ color, fontSize: "0.72rem", fontWeight: 700 }}>
              Bill fetched — amount, due date and file are locked. Only message can be edited.
            </Typography>
          </Box>
        )}
      </Box>

      {/* ── Post-fetch summary panel ──────────────────────────────────────── */}
      <Collapse in={billFetched} unmountOnExit>
        <Box
          sx={{
            borderRadius: 3, border: `2px solid ${color}`, overflow: "hidden", mb: 1,
            animation: "slideDown 0.3s ease",
            "@keyframes slideDown": {
              from: { opacity: 0, transform: "translateY(-6px)" },
              to:   { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          {/* Summary header */}
          <Box sx={{ background: `linear-gradient(135deg, ${color} 0%, ${color}cc 100%)`, px: 2.5, py: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CheckCircleIcon sx={{ color: "#fff", fontSize: 18 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#fff", fontSize: "0.85rem" }}>
                  Bill Fetched Successfully
                </Typography>
              </Box>
              <Chip
                label="Read-only"
                size="small"
                icon={<LockIcon sx={{ fontSize: 11, color: "rgba(255,255,255,0.7) !important" }} />}
                sx={{ height: 20, fontSize: "0.62rem", fontWeight: 700, backgroundColor: "rgba(255,255,255,0.2)", color: "#fff", "& .MuiChip-label": { px: 0.8 } }}
              />
            </Box>
          </Box>

          {/* Summary body — amount + due date */}
          <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap" }}>
            {/* Amount */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{ color, flexShrink: 0, display: "flex" }}>
                <AttachMoneyIcon sx={{ fontSize: 18 }} />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 600, fontSize: "0.68rem", display: "block", lineHeight: 1 }}>
                  Total Amount
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: "#0f172a" }}>
                  Rs. {displayAmount}
                </Typography>
              </Box>
            </Box>

            {/* Due Date */}
            {fetchedDueDate && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box sx={{ color, flexShrink: 0, display: "flex" }}>
                  <CalendarTodayIcon sx={{ fontSize: 18 }} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 600, fontSize: "0.68rem", display: "block", lineHeight: 1 }}>
                    Due Date
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: "#0f172a" }}>
                    {fetchedDueDate.format("DD MMM YYYY")}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}